import React from 'react';
import { Map, fromJS } from 'immutable';
import { Glyphicon } from 'react-bootstrap';

const createFormatter = (clazz, fields) => ({
  clazz,
  fields,
});

const createDictFormatter = (clazz, nameAlias, rest = {}) => createFormatter(clazz, {
  name: nameAlias,
  ...rest,
});

const createCounterpartyFormatter = (clazz, nameAlias, additional = {}) => createFormatter(clazz, {
  name: nameAlias,
  fax: 'Fax',
  email: 'Email',
  addressLine: 'Address',
  country: 'Country',
  city: 'City',
  telephone: 'Phone',
  personInCharge: 'Person in charge',
  postCode: 'Postal code',
  legalName: 'Legal name',
  yearOfStart: 'Year of relat. start',
  ...additional,
});

const buildFormatter = (formatter) => {
  const formatterClass = formatter.clazz;
  const formatterFields = formatter.fields;
  const obj = {};
  Object.keys(formatterFields).filter(f => f != 'DEFAULT')
    .forEach((field) => { obj[`${formatterClass}.${field}`] = formatterFields[field]; });
  obj[formatterClass] = formatterFields.DEFAULT;
  return obj;
};

const buildCombinedFormatters = (formatters) => {
  if (formatters instanceof Array) {
    return formatters.reduce((result, f) => result.merge(fromJS(buildFormatter(f))), new Map());
  }
  return new Map();
};

export const salePurchaseSharedFields = {
  ourBank: 'Our bank',
  interestRate: 'Interest rate',
  dealCanceled: 'Deal canceled',
  isArchived: 'Archived',
  creationTime: 'Date created',
  ops: 'Ops',
  contractDate: 'Contract date',
  creator: 'Creator',
  producers: 'Producer',
  bank: 'Our bank',
  regNumber: 'Contract number',
  counterpartyBank: 'Counterparty bank',
  responsibles: 'Responsible',
  agreedInWriting: 'Agreed in writing',
  agreedVerbally: 'Agreed verbally',
  contactSigned: 'Contract signed',
  hasDispute: 'Dispute',
};

export const purchaseFormatter = createFormatter(
  'ru.sggr.starter.deal.purchase.Purchase',
  {
    ...salePurchaseSharedFields,
    purchaseDate: 'Contract date',
    supplier: 'Seller',
    origin: 'Origin',
    DEFAULT: 'Purchase',
  },
);

export const saleFormatter = createFormatter(
  'ru.sggr.starter.deal.sale.Sale',
  {
    ...salePurchaseSharedFields,
    saleDate: 'Contract date',
    buyer: 'Buyer',
    destination: 'Destination',
    DEFAULT: 'Sale',
  },
);

export const portFormatter = createFormatter(
  'ru.sggr.starter.deal.port.Port',
  {
    billLandingDate: 'Bill lading date',
    eta: 'ETA',
    etc: 'ETC',
    destinationType: 'Destination type',
    dispatchType: 'Dispatch type',
    place: 'Place',
    status: 'Status',
    termModificator: 'Term modificator',
    detentionCurrency: 'Detention currency',
    demurrageCurrency: 'Demurrage currency',
    dispatchCurrency: 'Dispatch currency',
    demurrageAmount: 'Demurrage amount',
    dispatchAmount: 'Dispatch amount',
    sailingDate: 'Sailing date',
    commencement: 'Commencement',
    comment: 'Comment',
    rate: 'Term rate',
    laycanStart: 'Laycan date from',
    laycanEnd: 'Laycan date to',
    loadingTerm: 'Loading term',
    dischargeTerm: 'Discharge term',
    loadingTermModificator: 'Loading term modificator',
    dischargeTermModificator: 'Discharge term modificator',
    DEFAULT: 'Port',
  },
);

export const transportFormatter = createFormatter(
  'ru.sggr.starter.deal.transport.Transport',
  {
    DEFAULT: 'Transport',
    isArchived: 'Archived',
    transportType: 'Transport type',
    status: 'Status',
    responsibles: 'Responsible',
    externalResponsibles: 'External responsible',
    creationTime: 'Created date',
    creator: 'Creator',
    datex: 'Dachex',
    laycanDate: 'Laycan date',
    vesselName: 'Vessel name',
    voyageNumber: 'Voyage number',
    vesselStatus: 'Vessel status',
    vesselBroker: 'Ship broker',
    owner: 'Ship owner',
    freightCurrency: 'Freight currency',
    freightAmount: 'Freight',
    charterParty: 'Charterparty date',
    cutOffDate: 'Cut off date',
    onBoardDate: 'Shipment date',
    freeDemurrage: 'Free demurrage',
    freeDetention: 'Free detention',
    dischargeDate: 'Discharge date',
    forwarder: 'Forwarder',
    shippingline: 'Shipping line',
    shipmentDate: 'Shipment date',
    productType: 'Product type',
    product: 'Product',
    linkedProducts: 'Linked products',
    loadingPort: 'Loading at',
    dischargePort: 'Discharge at',
    loadingPortComment: 'Loading port comment',
    loadingPortStatus: 'Loading port status',
    dischargePortComment: 'Discharge port comment',
    dischargePortStatus: 'Discharge port status',
    loadingSailingDate: 'Loading sailing date',
    dischargeSailingDate: 'Discharge sailing date',
    billLandingDate: 'Bill of lading date',
    loadingRate: 'Loading rate',
    loadingTerm: 'Loading term',
    loadingTermModificator: 'Loading term modificator',
    dischargeTermModificator: 'Discharge term modificator',
    dischargeRate: 'Discharge rate',
    dischargeTerm: 'Discharge term',
    etaDischarge: 'ETA discharge port',
    detentionCurrency: 'Detention currency',
    detentionAmount: 'Detention amount',
    demurrageCurrency: 'Demurrege currency',
    demurrageAmount: 'Demurrage amount',
    dispatchCurrency: 'Dispatch currency',
    dispatchAmount: 'Dispatch amount',
    shipBroker: 'Ship broker',
    shipOwner: 'Ship owner',
    hasDispute: 'Dispute',
    dwt: 'DWT',
    freightLSAmount: 'Freight LS amount',
    isFreightLS: 'PT/LS',
  },
);

export const transportNameVoyageFormatter = createFormatter(
  'ru.sggr.starter.deal.transport.TransportNameVoyage',
  {
    DEFAULT: 'Transport name voyage',
    vesselName: 'Vessel name',
    voyageNumber: 'Voyage number',
  },
);

export const fileFormatter = createFormatter(
  'ru.sggr.starter.file.File',
  {
    DEFAULT: 'File',
    originalName: 'File name',
    regNumber: 'Document name',
  }
);

export const productTypeFormatter = createFormatter(
  'ru.sggr.starter.dict.complex.product.ProductType',
  {
    DEFAULT: 'Product type',
    name: 'Product type',
    fertecon: 'Fertecon',
    profercy: 'Profercy',
  },
);

export const compositeProductFormatter = createFormatter(
  'ru.sggr.starter.deal.compositeproduct.CompositeProduct',
  {
    DEFAULT: 'Product',
    purchase: 'Purchase',
    sale: 'Sale',
    transportType: 'Transport type',
    dispatchType: 'Dispatch type',
    term: 'Loading term',
    rate: 'Loading/Discharge terms rate',
    termModificator: 'Term modificator',
    detentionCurrency: 'Detention currency',
    detentionAmount: 'Detention amount',
    demurrageCurrency: 'Demurrage currency',
    demurrageAmount: 'Demurrage amount',
    dispatchCurrency: 'Dispatch currency',
    dispatchAmount: 'Dispatch amount',
    product: 'Product',
    productType: 'Product type',
    quantity: 'Quantity',
    quantityMin: 'Quantity min',
    quantityMax: 'Quantity max',
    quantityMinP: 'Quantity min percent',
    quantityMaxP: 'Quantity max percent',
    contractOption: 'Contract option',
    packing: 'Packing',
    shipmentFrom: 'Shipment from',
    shipmentTo: 'Shipment to',
    deliveryTerm: 'Delivery term',
    payment: 'Payment',
    lcReference: 'LC Reference',
    specification: 'Remarks',
    latestPaymentDate: 'LC Opening Deadline',
    priceCurrency: 'Price currency',
    priceValue: 'Price value',
    fixedPrice: 'Fixed price',
    priceFormula: 'Price formula',
    purchaseLink: 'Purchase link',
    saleLink: 'Sale link',
    ports: 'Port',
    expenses: 'Expenses',
    payments: 'Payments',
    userFobNetback: 'User FOB Netback',
    customPaymentText: 'Payment',
    carInsurance: 'Cargo insurance',
    completedLink: 'Set 0',
    shrinkage: 'Shrinkage',
    shrinkageIsPercentage: 'Shrinkage is percent',
    shrinkagePercent: 'Shrinkage percent',
  },
);

const linkFormatter = createFormatter(
  'ru.sggr.starter.deal.link.Link',
  {
    DEFAULT: 'Link',
    quantity: 'Quantity',
    quantityMin: 'Quantity min',
    quantityMax: 'Quantity max',
    quantityMinP: 'Quantity min percent',
    quantityMaxP: 'Quantity max percent',
    purchaseProduct: 'Purchase product',
    saleProduct: 'Sale product',
    transport: 'Transport',
    billLandingQuantity: 'BL Quantity',
    noTransportBl: 'Link without transport',
    fromMultiNodal: 'Created from multi-nodal',
  },
);

const interestValueFormatter = createFormatter(
  'ru.sggr.starter.deal.compositeproduct.InterestCalcValue',
  {
    DEFAULT: 'Values for interest calculation',
    value: 'Value',
    date: 'Date',
    currency: 'Currency',
  });

const multiNodalRouteFormatter = createFormatter(
  'ru.sggr.starter.deal.compositeproduct.MultiNodalRoute',
  {
    DEFAULT: 'Multi-nodal route',
    nodes: 'Nodes',
  });

const dealCurrencyRatesFormatter = createFormatter(
  'ru.sggr.starter.deal.DealCurrencyRates',
  {
    DEFAULT: 'Currency rates',
    value: 'Symbol',
    rate: 'Rate',
  });
const multiNodalNodeFormatter = createFormatter(
  'ru.sggr.starter.deal.compositeproduct.MultiNodalElement',
  {
    DEFAULT: 'Multi-nodal node',
    shipmentStart: 'Transshipment Date',
    shipmentEnd: 'Arrival',
    destinationFrom: 'Destination from',
    destinationTo: 'Destination to',
    finalDestination: 'Final destination',
    currency: 'Currency',
    costEstimated: 'Cost Estimated',
    costActual: 'Cost Actual',
    term: 'Incoterm',
    mode: 'Mode',
    hasArrived: 'Arrived',
    quantity: 'Qty',
    carrier: 'Carrier',
    blDate: 'BL Date',
  });

const paymentFormatter = createFormatter(
  'ru.sggr.starter.deal.payments.Payments',
  {
    DEFAULT: 'Payment',
    topManagerApproveState: 'Top manager approve state',
    topApprover: 'Top approver',
    topApproverLinkExpireTime: 'Top approver link expire time',
    approveByTopManager: 'Approved by top manager',
    approveComment: 'Top manager approver comment',
    topApproverNote: 'Top manager note',
    paymentStatus: 'Payment status',
    approve: 'Approve',
    contractNumber: 'Contract number',
    documentNumber: 'Document number',
    expensesType: 'Expense type',
    counterparty: 'Counterparty',
    isDebit: 'DR/CR',
    dueAmountCurrency: 'Due amount currency',
    dueAmount: 'Due amount',
    dueDate: 'Due date',
    bankingCost: 'Banking cost',
    paymentAmount: 'Payment amount',
    paymentDate: 'Payment date',
    bank: 'Bank',
    document: 'Document',
    documentType: 'Document type',
    notes: 'Notes',
    bankRepaymentDate: 'Bank repayment date',
    estimatedDueDate: 'Estimated due date',
    fullCostPercent: 'Full cost percent',
    paymentMethod: 'Payment method',
    estimatedDueAmount: 'Estimated due amount',
    creationDate: 'Creation date',
  },
);

const expensesFormatter = createFormatter(
  'ru.sggr.starter.deal.expenses.Expenses',
  {
    DEFAULT: 'Expense',
    isDebit: 'DR/CR',
    expensesType: 'Expense type',
    summType: 'Sum type',
    estimateCurrency: 'Estimate currency',
    actualCurrency: 'Actual currency',
    estimate: 'Estimate value',
    actual: 'Actual value',
  },
);

const userFormatter = createFormatter(
  'ru.sggr.starter.user.User',
  {
    DEFAULT: 'User',
    username: 'User name',
  },
);


const expensesTypeFormatter = createDictFormatter(
  'ru.sggr.starter.dict.complex.expensestype.ExpensesType',
  'Expense type', {
    isSale: 'Sale',
    isPurchase: 'Purchase',
    isTransport: 'Transport',
    common: 'Common',
    expensesCategory: 'Expense category',
  });

const buyerFormatter = createCounterpartyFormatter(
  'ru.sggr.starter.dict.counterparty.buyer.Buyer', 'Buyer', {
    carInsurance: 'Cargo insurance',
  });

const FORMATTERS = [
  purchaseFormatter,
  saleFormatter,
  portFormatter,
  transportFormatter,
  transportNameVoyageFormatter,
  compositeProductFormatter,
  productTypeFormatter,
  fileFormatter,
  linkFormatter,
  paymentFormatter,
  expensesFormatter,
  interestValueFormatter,
  multiNodalRouteFormatter,
  multiNodalNodeFormatter,
  dealCurrencyRatesFormatter,
  userFormatter,
  createDictFormatter('ru.sggr.starter.dict.simple.place.Place', 'Port', {
    region: 'Region',
    portGroups: 'Port group',
  }),
  createDictFormatter('ru.sggr.starter.dict.simple.portgroup.PortGroup', 'Port group', {
    ports: 'Port',
  }),
  createDictFormatter('ru.sggr.starter.dict.counterparty.bank.Bank', 'Bank'),
  buyerFormatter,
  createDictFormatter('ru.sggr.starter.dict.simple.commoncounterparties.CommonCounterparty',
    'Common counterparty'),
  createDictFormatter('ru.sggr.starter.dict.simple.deliveryterm.DeliveryTerm',
    'Delivery term'),
  createDictFormatter('ru.sggr.starter.dict.simple.documenttype.DocumentType',
    'Document type'),
  createDictFormatter('ru.sggr.starter.dict.counterparty.employee.Employee',
    'Employee'),
  expensesTypeFormatter,
  createCounterpartyFormatter('ru.sggr.starter.dict.counterparty.forwarder.Forwarder',
    'Forwarder'),
  createCounterpartyFormatter(
    'ru.sggr.starter.dict.counterparty.inspectioncompany.InspectionCompany',
    'Inspection company'),
  createCounterpartyFormatter('ru.sggr.starter.dict.counterparty.insurancecompany.InsuranceCompany',
    'Insurance company'),
  createDictFormatter('ru.sggr.starter.dict.simple.loadingratecondition.LoadingTerm',
    'Loading term'),
  createDictFormatter('ru.sggr.starter.dict.simple.origin.Origin', 'Origin', {
    region: 'Region',
  }),
  createDictFormatter('ru.sggr.starter.dict.simple.region.Region', 'Region', {
    coordinates: 'Coordinates',
    ports: 'Ports',
  }),
  createCounterpartyFormatter('ru.sggr.starter.dict.counterparty.owner.Owner', 'Owner'),
  createDictFormatter('ru.sggr.starter.dict.simple.packing.Packing', 'Packing'),
  createCounterpartyFormatter('ru.sggr.starter.dict.counterparty.producer.Producer', 'Producer'),
  createDictFormatter('ru.sggr.starter.dict.complex.product.Product', 'Product'),
  createDictFormatter('ru.sggr.starter.dict.complex.product.ProductType', 'Product type'),
  createCounterpartyFormatter('ru.sggr.starter.dict.counterparty.shippingline.ShippingLine',
    'Shipping line'),
  createCounterpartyFormatter('ru.sggr.starter.dict.counterparty.supplier.Supplier', 'Seller'),
  createDictFormatter('ru.sggr.starter.dict.simple.topmanager.TopManager', 'Top manager'),
  createDictFormatter('ru.sggr.starter.dict.counterparty.tradecounterpartties.TradeCounterparty',
    'Trade counterparty', {
      expensesType: 'Expense type',
    }),
  createCounterpartyFormatter('ru.sggr.starter.dict.counterparty.vesselbroker.VesselBroker',
    'Vessel broker'),
];

const FIELD_VALUE_MAPPER = {};

export const fieldValueFormatter = (field, inputValue) => {
  if (!inputValue) {
    return inputValue;
  }
  const valueMap = FIELD_VALUE_MAPPER[field];
  if (valueMap) {
    return valueMap[inputValue.toString()];
  }
  return inputValue;
};

const addValueMap = (clazz, field, mapData) => {
  FIELD_VALUE_MAPPER[`${clazz}.${field}`] = mapData;
};

const booleanValueMapper = {
  true: <Glyphicon glyph="check" />,
  false: <Glyphicon glyph="unchecked" />,
};
const drCrValueMapper = {
  true: <Glyphicon glyph="minus" />,
  false: <Glyphicon glyph="plus" />,
};
const transportTypeMapper = {
  RAILCAR: 'Railcar',
  TRUCK: 'Truck',
  BULK: 'Bulk Vessel',
  CONTAINER: 'Container',
};

const PTLSFormatter = {
  true: 'LS',
  false: 'PT',
};

addValueMap(compositeProductFormatter.clazz, 'fixedPrice', {
  true: 'Fix',
  false: 'Formula',
});

addValueMap(compositeProductFormatter.clazz, 'transportType', transportTypeMapper);
addValueMap(compositeProductFormatter.clazz, 'completedLink', booleanValueMapper);
addValueMap(compositeProductFormatter.clazz, 'carInsurance', booleanValueMapper);
addValueMap(compositeProductFormatter.clazz, 'shrinkageIsPercentage', booleanValueMapper);
addValueMap(transportFormatter.clazz, 'transportType', transportTypeMapper);
addValueMap(transportFormatter.clazz, 'Datex', booleanValueMapper);
addValueMap(transportFormatter.clazz, 'hasDispute', booleanValueMapper);
addValueMap(portFormatter.clazz, 'destinationType', {
  LOADING: 'Loading',
  DISCHARGE: 'Discharge',
});
addValueMap(compositeProductFormatter.clazz, 'contractOption', {
  MUTUAL_OPTION: 'Mutual option',
  BUYER_OPTION: 'Buyer option',
  SELLER_OPTION: 'Seller option',
});
addValueMap(expensesFormatter.clazz, 'isDebit', drCrValueMapper);
addValueMap(paymentFormatter.clazz, 'isDebit', drCrValueMapper);
addValueMap(paymentFormatter.clazz, 'approve', booleanValueMapper);
addValueMap(paymentFormatter.clazz, 'topManagerApproveState', {
  NOT_SET: 'Not set',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
});
addValueMap(purchaseFormatter.clazz, 'agreedVerbally', booleanValueMapper);
addValueMap(purchaseFormatter.clazz, 'agreedInWriting', booleanValueMapper);
addValueMap(purchaseFormatter.clazz, 'contactSigned', booleanValueMapper);
addValueMap(purchaseFormatter.clazz, 'isArchived', booleanValueMapper);
addValueMap(purchaseFormatter.clazz, 'hasDispute', booleanValueMapper);
addValueMap(purchaseFormatter.clazz, 'dealCanceled', booleanValueMapper);

addValueMap(saleFormatter.clazz, 'agreedVerbally', booleanValueMapper);
addValueMap(saleFormatter.clazz, 'agreedInWriting', booleanValueMapper);
addValueMap(linkFormatter.clazz, 'noTransportBl', booleanValueMapper);
addValueMap(linkFormatter.clazz, 'fromMultiNodal', booleanValueMapper);
addValueMap(saleFormatter.clazz, 'contactSigned', booleanValueMapper);
addValueMap(saleFormatter.clazz, 'isArchived', booleanValueMapper);
addValueMap(saleFormatter.clazz, 'hasDispute', booleanValueMapper);
addValueMap(saleFormatter.clazz, 'dealCanceled', booleanValueMapper);

addValueMap(transportFormatter.clazz, 'isArchived', booleanValueMapper);
addValueMap(transportFormatter.clazz, 'datex', booleanValueMapper);
addValueMap(transportFormatter.clazz, 'isFreightLS', PTLSFormatter);

addValueMap(expensesTypeFormatter.clazz, 'isPurchase', booleanValueMapper);
addValueMap(expensesTypeFormatter.clazz, 'isSale', booleanValueMapper);
addValueMap(expensesTypeFormatter.clazz, 'isTransport', booleanValueMapper);
addValueMap(expensesTypeFormatter.clazz, 'common', booleanValueMapper);

addValueMap(buyerFormatter.clazz, 'carInsurance', booleanValueMapper);

export default buildCombinedFormatters(FORMATTERS);
